import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import {
    Breadcrumb,
    Layout,
    Spin,
    Card,
    Row,
    Col,
    Table, // Added Table
    Tag, // Added Tag for status rendering example
    Button, // Added Button
    Form, // Added Form
    Input, // Added Input
    DatePicker,
    Modal,
    Select, // Added Select
    Space, // Added Space
    Descriptions, // Added Descriptions
    theme, // Added theme for token
} from 'antd';
import axios from 'axios';
import dayjs from 'dayjs'; // Added dayjs for date formatting
import { APPBACKGROUND_COLOR, MAINAPIURL } from '../../appsettings';
import { AlertError } from '../../usableclasses';
import Headerdiv from '../../layout/header';
import Footerdiv from '../../layout/footer';

const { Content } = Layout;
const { RangePicker } = DatePicker; // Added RangePicker
const { Option } = Select; // Added Option for Select

const Transactions = () => {
    const navigate = useNavigate();
    const [form] = Form.useForm(); // Form instance for filter controls
    const [loading, setLoading] = useState(true);
    const [tableData, setTableData] = useState([]); // Data for the table
    const [totalCount, setTotalCount] = useState(0); // Total number of records for pagination
    const [page, setPage] = useState(1); // Current page number (1-indexed)
    const [pageSize, setPageSize] = useState(10); // Number of items per page
    const ApiURL = MAINAPIURL;
    const TOKEN = localStorage.getItem("AUTHCODE");
    const [isModalVisible, setIsModalVisible] = useState(false);
    const [selectedTransaction, setSelectedTransaction] = useState(null);
    const { token } = theme.useToken(); // Get theme tokens

    useEffect(() => {
        // Fetch transactions when the component mounts or when page/pageSize changes
        // For the initial fetch, filterValues will be empty, pageNum will be 1, pSize will be 10
        fetchTransactions({}, page, pageSize);
    }, []); // Initial fetch on component mount

    const fetchTransactions = async (filterValues = {}, pageNum = 1, pSize = 10) => {
        setLoading(true);
        let transDateStartFormatted = "";
        let transDateEndFormatted = "";

        if (filterValues.dateRange && filterValues.dateRange.length === 2) {
            // Ensure dateRange values are dayjs objects before formatting
            transDateStartFormatted = dayjs.isDayjs(filterValues.dateRange[0])
                ? filterValues.dateRange[0].format("YYYY-MM-DD")
                : "";
            transDateEndFormatted = dayjs.isDayjs(filterValues.dateRange[1])
                ? filterValues.dateRange[1].format("YYYY-MM-DD")
                : "";
        }

        const body = {
            invoiceRef: filterValues.invoiceRef || "",
            paymentFrom: filterValues.paymentFrom || "",
            bankReferance: filterValues.bankReferance || "",
            transDateStart: transDateStartFormatted,
            transDateEnd: transDateEndFormatted,
            pageLength: pSize,
            pageNumber: pageNum
        };

        try {
            const headers = {
                SystemAccessToken: TOKEN
            };
            // Assuming the endpoint expects a POST request for filtering
            const response = await axios.post(`${ApiURL}/orders/ipgpaymentfilter`, body, { headers });

            // Assuming API response structure: { dataList: [], rowcount: 0 }
            // Adjust if your API response is different
            setTableData(response.data.dataList || []);
            setTotalCount(response.data.rowcount || 0);
        } catch (error) {
            AlertError("Failed to fetch transactions: " + (error.response?.data?.message || error.message || "Unknown error"));
            setTableData([]); // Clear data on error
            setTotalCount(0); // Reset count on error
        } finally {
            setLoading(false);
        }
    };

    // Handler for form submission (Filter button)
    const onFinish = (values) => {
        setPage(1); // Reset to first page on new filter
        fetchTransactions(values, 1, pageSize);
    };

    // Handler for clearing filters
    const handleClear = () => {
        form.resetFields();
        setPage(1); // Reset to first page
        fetchTransactions({}, 1, pageSize); // Fetch all data
    };

    const columns = [
        {
            title: 'Invoice No',
            dataIndex: 'invoiceNo',
            key: 'invoiceNo',
        },
        {
            title: 'Log Date Time',
            dataIndex: 'logDateTime',
            key: 'logDateTime',
        },

        {
            title: 'Trans From',
            dataIndex: 'transFrom',
            key: 'transFrom',
        },
        {
            title: 'Card Type',
            dataIndex: 'cardType',
            key: 'cardType',
        },
        {
            title: 'Card No',
            dataIndex: 'cardNo',
            key: 'cardNo',
        },
        {
            title: 'Paid Amount',
            dataIndex: 'paidAmount',
            key: 'paidAmount',
        },
        {
            title: 'Bank Transaction Ref',
            dataIndex: 'bankTransactionReferance',
            key: 'bankTransactionReferance',
        },
        {
            title: 'Action',
            key: 'action',
            render: (_, record) => (
                <Button type="primary" variant='solid' onClick={() => {
                    setSelectedTransaction(record);
                    setIsModalVisible(true);
                }}>
                    View
                </Button>
            ),
        },
    ];

    return (
        <Spin spinning={loading} tip="Loading transactions details...">
            <Layout style={{ backgroundColor: APPBACKGROUND_COLOR }}>
                <Headerdiv />
                <Content style={{ padding: '10px' }}>
                    <Breadcrumb style={{ margin: '16px 0' }}>
                        <Breadcrumb.Item><Link to={'/orders'}>MLM Orders</Link></Breadcrumb.Item>
                        <Breadcrumb.Item>Transactions</Breadcrumb.Item>
                    </Breadcrumb>

                    <Card style={{ marginBottom: 16 }}>
                        <Form form={form} onFinish={onFinish} layout="vertical">
                            <Row gutter={16}>
                                <Col xs={24} sm={12} lg={6}>
                                    <Form.Item name="invoiceRef" label="Invoice Reference">
                                        <Input placeholder="Enter Invoice Reference" allowClear />
                                    </Form.Item>
                                </Col>
                                <Col xs={24} sm={12} lg={6}>
                                    <Form.Item name="paymentFrom" label="Payment From">
                                        {/* Changed from Input to Select */}
                                        <Select placeholder="Select Payment Source" allowClear>
                                            {/* Hardcoded option as per request */}
                                            <Option value="CYBERSOURCE">CYBERSOURCE</Option>
                                            <Option value="PAYCORP">PAYCORP</Option>
                                        </Select>
                                    </Form.Item>
                                </Col>
                                <Col xs={24} sm={12} lg={6}>
                                    <Form.Item name="bankReferance" label="Bank Reference">
                                        <Input placeholder="Enter Bank Reference" allowClear />
                                    </Form.Item>
                                </Col>
                                <Col xs={24} sm={12} lg={6}>
                                    <Form.Item name="dateRange" label="Transaction Date Range">
                                        <RangePicker style={{ width: '100%' }} />
                                    </Form.Item>
                                </Col>
                            </Row>
                            {/* Changed to use a single Col with Space for better right alignment control */}
                            <Row justify="end" style={{ marginTop: 16 }}>
                                <Col>
                                    <Space>
                                        <Button type="primary" htmlType="submit">Filter</Button>
                                        <Button onClick={handleClear}>Clear</Button>
                                    </Space>
                                </Col>
                            </Row>
                        </Form>
                    </Card>

                    <Table
                        columns={columns}
                        dataSource={tableData}
                        rowKey={(record) => record.id || record.invoiceRef || Math.random()} // Ensure unique key, fallback to random if no unique ID
                        loading={loading}
                        pagination={{
                            current: page,
                            pageSize: pageSize,
                            total: totalCount,
                            onChange: (p, ps) => { setPage(p); setPageSize(ps); fetchTransactions(form.getFieldsValue(), p, ps); },
                        }}
                        scroll={{ x: 'max-content' }} // For horizontal scrolling on smaller screens
                    />

                    {selectedTransaction && (
                        <Modal
                            title="Transaction Details"
                            visible={isModalVisible}
                            onCancel={() => setIsModalVisible(false)}
                            footer={[
                                <Button key="close" onClick={() => setIsModalVisible(false)}>
                                    Close
                                </Button>,
                            ]}
                            width="80%" // Make the modal wider
                            bodyStyle={{ 
                                maxHeight: '70vh', 
                                overflowY: 'auto',
                                backgroundColor: APPBACKGROUND_COLOR // Match page background
                            }} 
                        >
                            {/* Wrap Descriptions in a div with white background to mimic a Card */}
                            <div style={{ 
                                backgroundColor: token.colorBgContainer, 
                                padding: '16px', // Adjust padding as needed
                                borderRadius: token.borderRadiusLG 
                            }}>
                                <Descriptions bordered column={1} size="small" layout="horizontal">
                                    <Descriptions.Item label="Invoice No">{selectedTransaction.invoiceNo}</Descriptions.Item>
                                    <Descriptions.Item label="Log Date Time">{selectedTransaction.logDateTime}</Descriptions.Item>
                                    <Descriptions.Item label="Trans From">{selectedTransaction.transFrom}</Descriptions.Item>
                                    <Descriptions.Item label="Card Type">{selectedTransaction.cardType}</Descriptions.Item>
                                    <Descriptions.Item label="Card No">{selectedTransaction.cardNo}</Descriptions.Item>
                                    <Descriptions.Item label="Paid Amount">{selectedTransaction.paidAmount}</Descriptions.Item>
                                    <Descriptions.Item label="Bank Transaction Ref">{selectedTransaction.bankTransactionReferance}</Descriptions.Item>
                                    <Descriptions.Item label="Transaction Message">{selectedTransaction.transMessage}</Descriptions.Item>
                                    <Descriptions.Item label="Transaction Type">{selectedTransaction.transactionType}</Descriptions.Item>
                                    <Descriptions.Item label="Decision">{selectedTransaction.decision}</Descriptions.Item>
                                    <Descriptions.Item label="Response Code">{selectedTransaction.responseCode}</Descriptions.Item>
                                    <Descriptions.Item label="Bank Bill Ref">{selectedTransaction.bankBillReferance}</Descriptions.Item>
                                    <Descriptions.Item label="Profile ID">{selectedTransaction.profileId}</Descriptions.Item>
                                    <Descriptions.Item label="Card Expiry">{selectedTransaction.cardExpire}</Descriptions.Item>
                                    <Descriptions.Item label="Bank Commit Time">{new Date(selectedTransaction.bankCommitIntTime).toLocaleString()}</Descriptions.Item>
                                </Descriptions>
                            </div>
                        </Modal>
                    )}
                </Content>
                <Footerdiv />
            </Layout>
        </Spin>
    );
};

export default Transactions;
